/**
 * Type definitions for UI Blocks streaming contract
 * Based on JSON_SCHEMA_FOR_UI_STREAMING.md
 */

// Button variants and sizes for confirmation actions
export type ButtonVariant =
  | "default" | "primary" | "outlinePrimary" | "destructive" | "outline"
  | "red" | "white" | "green" | "secondary" | "ghost" | "link" | "black" | "badge";

export type ButtonSize = "default" | "sm" | "lg" | "icon";

// Text Block Types
export type TextBlock = {
  type: "text";
  version: string;
  content: string;
};

// Confirmation Block Types
export type ConfirmationAction = {
  label: string;
  value: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
};

export type ConfirmationContent = {
  label?: string;
  description?: string;
  confirmAction: ConfirmationAction;
  cancelAction?: ConfirmationAction;
  /** Set from tool_call_outputs when loading history — indicates which action the user chose */
  resolvedAction?: { value: string; label: string };
};

export type ConfirmationBlock = {
  type: "confirmation";
  version: string;
  content: ConfirmationContent;
};

// Connect Block Types (card offering disconnected apps to connect)
export type ConnectAppRef = {
  short_name: string;
  reason?: string | null;
};

export type ConnectContent = {
  apps: ConnectAppRef[];
};

export type ConnectBlock = {
  type: "connect";
  version: string;
  content: ConnectContent;
};

// Inline Suggestions Block Types (interactive suggestion pills)
export type InlineSuggestionsItem = {
  label: string;
  value: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  shouldRedirect?: boolean; // if true, indicates this should redirect to chat window before posting
  colorVariant?: "red" | "green" | "yellow" | "gray"; // colored border variant to match card severity
};

export type InlineSuggestionsContent = {
  label?: string;
  items: InlineSuggestionsItem[];
};

export type InlineSuggestionsBlock = {
  type: "inline_suggestions";
  version: string;
  content: InlineSuggestionsContent;
};

// Metrics Grid Block Types
export type MetricsGridCell = {
  value: string | number; // formatted metric value
  change?: string | number; // change indicator (e.g., "+5.2%", "-3")
  progress?: string | number; // progress indicator (e.g., "75%", "3/4")
  label?: string; // metric label (e.g., "Click-Through Rate")
  changeColor?: string; // Tailwind color class for change indicator
};

export type MetricsGridContent = {
  header?: string; // optional header text (e.g., "performance vs baseline")
  cells: MetricsGridCell[];
  columns?: 1 | 2 | 3 | 4; // number of columns (default: 3)
};

export type MetricsGridBlock = {
  type: "metrics_grid";
  version: string;
  content: MetricsGridContent;
};

// Tabbed Chart Block Types
export type TabbedChartTab = {
  id: string;
  label: string;
  chartType: 'line' | 'area' | 'bar' | 'pie' | 'radial' | 'scatter';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartProps: Record<string, any>;
};

export type TabbedChartContent = {
  title?: string;
  description?: string;
  defaultTabId?: string;
  height?: number;
  tabs: TabbedChartTab[];
  referenceLines?: ChartReferenceLine[];
  referenceDots?: ChartReferenceDot[];
  referenceAreas?: ChartReferenceArea[];
};

export type TabbedChartBlock = {
  type: "tabbed_chart";
  version: string;
  content: TabbedChartContent;
};

// Select Block Types
export type SelectOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type SelectContent = {
  label?: string;
  description?: string;
  options: SelectOption[];
  selectedValues?: string[];
  layout?: "vertical" | "grid";
  columns?: 2 | 3 | 4;
};

export type SelectBlock = {
  type: "select";
  version: string;
  content: SelectContent;
};

// Radio Block Types (single selection)
export type RadioOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type RadioContent = {
  label?: string;
  description?: string;
  options: RadioOption[];
  selectedValue?: string;
  layout?: "vertical" | "grid";
  columns?: 2 | 3 | 4;
};

export type RadioBlock = {
  type: "radio";
  version: string;
  content: RadioContent;
};

// Editable List Block Types
export type EditableListMode = "string" | "grouped";

export type EditableStringListContent = {
  mode?: "string"; // default
  label?: string;
  items: string[];
  allowAdd?: boolean;
  allowRemove?: boolean;
  inputPlaceholder?: string;
  addButtonText?: string;
  applyButtonText?: string;
  cancelButtonText?: string;
};

export type EditableGroupedListGroup = {
  label?: string;
  items: string[];
};

export type EditableGroupedListContent = {
  mode: "grouped";
  label?: string;
  groups: EditableGroupedListGroup[];
  allowAdd?: boolean;
  allowRemove?: boolean;
  inputPlaceholder?: string;
  addButtonText?: string;
  applyButtonText?: string;
  cancelButtonText?: string;
};

export type EditableListContent =
  | EditableStringListContent
  | EditableGroupedListContent;

export type EditableListBlock = {
  type: "editable_list";
  version: string;
  content: EditableListContent;
};

// Chart Block Types
//
// These types still describe the legacy `block.content` shape that
// `@strivelabs/ui-blocks` chart wrappers consume (dynamic-key rows +
// `config`/`lines`/`bars`/`areas` arrays). Older demos / playground /
// report screens still construct charts in this shape directly.
//
// The chat path's `block.content` arrives in a DIFFERENT canonical shape
// (mirroring the workflow-apps Zod schema). `chart-block-renderer.tsx`
// declares that wire shape internally and adapts it to these legacy
// types before forwarding to the ui-blocks wrappers. See the file-level
// comment in `chart-block-renderer.tsx` for the adapter's role.
export type ChartConfig = {
  [key: string]: {
    label?: string;
    color?: string;
  };
};

export type LineConfig = {
  dataKey: string;
  stroke?: string;
  strokeWidth?: number;
  dot?: boolean;
  type?: 'monotone' | 'linear' | 'step' | 'stepBefore' | 'stepAfter';
};

export type LineChartContent = {
  data: Record<string, unknown>[];
  xAxisKey: string;
  config?: ChartConfig;
  lines?: LineConfig[];
  title?: string;
  description?: string;
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  referenceLines?: ChartReferenceLine[];
  referenceDots?: ChartReferenceDot[];
  referenceAreas?: ChartReferenceArea[];
};

export type LineChartBlock = {
  type: "line_chart";
  version: string;
  content: LineChartContent;
};

export type DataKey = {
  key: string;
  name?: string;
  color?: string;
};

export type BarChartContent = {
  data: Record<string, string | number>[];
  xAxisKey: string;
  dataKeys?: DataKey[];
  title?: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  className?: string;
  referenceLines?: ChartReferenceLine[];
  referenceDots?: ChartReferenceDot[];
  referenceAreas?: ChartReferenceArea[];
};

export type BarChartBlock = {
  type: "bar_chart";
  version: string;
  content: BarChartContent;
};

export type AreaConfig = {
  dataKey: string;
  type?: 'monotone' | 'linear' | 'step' | 'stepBefore' | 'stepAfter';
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  fillOpacity?: number;
  stackId?: string;
};

export type AreaChartContent = {
  data: Record<string, unknown>[];
  xAxisKey: string;
  config?: ChartConfig;
  areas?: AreaConfig[];
  title?: string;
  description?: string;
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  referenceLines?: ChartReferenceLine[];
  referenceDots?: ChartReferenceDot[];
  referenceAreas?: ChartReferenceArea[];
};

export type AreaChartBlock = {
  type: "area_chart";
  version: string;
  content: AreaChartContent;
};

export type PieChartContent = {
  data: Record<string, unknown>[];
  dataKey: string;
  nameKey: string;
  config?: ChartConfig;
  title?: string;
  description?: string;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  innerRadius?: number;
  outerRadius?: number;
  paddingAngle?: number;
};

export type PieChartBlock = {
  type: "pie_chart";
  version: string;
  content: PieChartContent;
};

export type RadialChartContent = {
  data: Record<string, unknown>[];
  dataKey: string;
  nameKey?: string;
  config?: ChartConfig;
  title?: string;
  description?: string;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  innerRadius?: number | string;
  outerRadius?: number | string;
  startAngle?: number;
  endAngle?: number;
};

export type RadialChartBlock = {
  type: "radial_chart";
  version: string;
  content: RadialChartContent;
};

export type ScatterSeries = {
  name: string;
  data: Record<string, unknown>[];
  fill?: string;
  shape?: 'circle' | 'cross' | 'diamond' | 'square' | 'star' | 'triangle' | 'wye';
};

export type ScatterChartContent = {
  data: Record<string, unknown>[];
  xAxisKey: string;
  yAxisKey: string;
  scatters: ScatterSeries[];
  config?: ChartConfig;
  title?: string;
  description?: string;
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  height?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
};

export type ScatterChartBlock = {
  type: "scatter_chart";
  version: string;
  content: ScatterChartContent;
};

// Table Block Types
export type ColoredCell = { value: string | number | null; color?: string };

export type MetricComparisonCell = {
  value: string | number | null;
  change: string | number | null;
  change_percentage: string | null;
  color?: string;
};

export type TableContent = {
  // Required
  headers: string[];
  rows: (string | number | null | ColoredCell)[][];

  // Optional
  title?: string;
  description?: string;
  variant?: "default" | "minimal" | "spacious";
  enableExport?: boolean;
  exportFilename?: string;
  className?: string;
};

export type TableBlock = {
  type: "table";
  version: string;
  content: TableContent;
};

// Page Reference for navigation within multi-page reports
export type PageRef = {
  page_group_id: string;
  page_id: string;
};

// Data Table Client Block Types (keyed columns + object rows with optional page navigation)
//
// Same dual-shape note as the chart types above: these legacy types stay for
// existing demo / report consumers. The chat path's wire shape (positional
// cells aligned with columns by index) is declared internally inside
// `data-table-client-block-renderer.tsx` and adapted to this legacy shape.
export type DataTableClientSortType = 'string' | 'currency' | 'percentage';

export type DataTableClientColumn = {
  key: string;
  label: string;
  /** Whether this column is sortable. Defaults to `false`. */
  enableSorting?: boolean;
  /**
   * How to interpret cell values when sorting. Defaults to `'string'`.
   * - `'string'`     – case-insensitive lexicographic sort
   * - `'currency'`   – strips symbols/commas (e.g. `$1,200.50` → `1200.5`) then sorts numerically
   * - `'percentage'` – strips `%` (e.g. `45.2%` → `45.2`) then sorts numerically
   */
  sortType?: DataTableClientSortType;
};

export type DataTableClientRow = {
  [key: string]: string | number | null | ColoredCell | MetricComparisonCell | PageRef | undefined;
  /** Optional reference to a page within a multi-page report. When present, the row is clickable and navigates to the referenced page. */
  __page_ref?: PageRef;
};

export type DataTableClientContent = {
  columns: DataTableClientColumn[];
  rows: DataTableClientRow[];
  title?: string;
  description?: string;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  fuzzySearch?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  emptyText?: string;
};

export type DataTableClientBlock = {
  type: "data_table_client";
  version: string;
  content: DataTableClientContent;
};

// Selectable Table Block Types (interactive — checkbox row selection + submit)
//
// Reuses the data_table_client column/row shapes. The user picks one or more
// rows and clicks Apply; the selected rows are emitted back to the agent via a
// UIActionEvent (see component: "selectable_table" below).
export type SelectableTableContent = {
  columns: DataTableClientColumn[];
  rows: DataTableClientRow[];
  /** Question/prompt shown above the table. */
  label?: string;
  /** Table title. Use either label or title. */
  title?: string;
  description?: string;
  /**
   * Column key whose value uniquely identifies each row. Reported back as the
   * row id on submit. When omitted, rows are identified by their position.
   */
  idKey?: string;
  /** Minimum rows that must be selected before Apply is enabled. Default: 1 */
  minSelected?: number;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  enablePagination?: boolean;
  pageSize?: number;
  emptyText?: string;
  applyButtonText?: string;
  cancelButtonText?: string;
};

export type SelectableTableBlock = {
  type: "selectable_table";
  version: string;
  content: SelectableTableContent;
};

// Stacked Suggestions Block Types
export type StackedSuggestion = {
  id: number;
  text: string;
  icon?: string; // lucide-react icon name (e.g., "Sparkles", "Brain", "Lightbulb") or emoji
  prefillOnly?: boolean; // defaults to false (auto-send)
};

export type StackedSuggestionsContent = {
  suggestions: StackedSuggestion[];
};

export type StackedSuggestionsBlock = {
  type: "stacked_suggestions";
  version: string;
  content: StackedSuggestionsContent;
};

// Container Block Types (Layout & Grouping)
// Supports masonry (newspaper columns), grid (fixed rows), and flex (wrapping) layouts
export type ContainerLayout = "masonry" | "grid" | "flex";
export type ContainerPadding = "none" | "sm" | "md" | "lg";
export type ContainerScroll = "auto" | "always" | "none";
export type ContainerBorderColor = "red" | "green" | "yellow" | "gray" | "blue" | "purple";

export type ContainerBlock = {
  type: "container";
  version: string;
  
  // Layout properties
  layout?: ContainerLayout;
  // "masonry": Newspaper-style vertical columns (content flows top-to-bottom)
  // "grid": Fixed grid rows (content flows left-to-right, wraps to new row)
  // "flex": Responsive wrapping (items wrap when space runs out, like tag clouds)
  gap?: number; // in pixels, default: 16
  columns?: 1 | 2 | 3 | 4; // when provided, use fixed columns; otherwise responsive free-flow
  padding?: ContainerPadding; // default: "none"
  maxWidth?: number | string;
  height?: number | string;
  scroll?: ContainerScroll; // default: "auto"

  // Grouping properties (semantic organization)
  id?: string;
  label?: string;
  border?: boolean; // default: false
  borderColor?: ContainerBorderColor; // colored left border (e.g., for insights cards)
  width?: number | string;

  // Header properties (optional heading with badge)
  heading?: string; // optional heading/title shown above the container (supports markdown)
  headingBadge?: string; // optional badge text shown next to heading
  description?: string; // optional description text below heading

  // Content blocks (at least 1 item)
  blocks: (UIBlock | ContainerBlock)[];
};

// ============================================================================
// Experiment Detail Types (used by experiments behaviour slot)
// ============================================================================

// Re-export experiment types from ui-blocks (single source of truth)
import type {
  ExperimentPerformanceMetric,
  ExperimentIssue,
  ExperimentChartConfig,
  ExperimentDetailedMetricRow,
  ExperimentAiInsight,
  ExperimentFollowUp,
  ExperimentExpectedResult,
  ExperimentSlotData,
  ChartReferenceLine,
  ChartReferenceDot,
  ChartReferenceArea,
} from '@strivelabs/ui-blocks';

export type {
  ExperimentPerformanceMetric,
  ExperimentIssue,
  ExperimentChartConfig,
  ExperimentDetailedMetricRow,
  ExperimentAiInsight,
  ExperimentFollowUp,
  ExperimentExpectedResult,
  ExperimentSlotData,
  ChartReferenceLine,
  ChartReferenceDot,
  ChartReferenceArea,
};

// ============================================================================
// Block Behaviour Types
// ============================================================================

/**
 * Action that triggers a chat prompt
 */
export type BehaviourChatAction = {
  id: string;
  type: 'chat';
  data: {
    prompt: string | string[];
    platform_context: unknown;
  };
};

/**
 * Action that renders an agent experience (e.g. insight_preview)
 */
export type BehaviourAgentExperienceAction = {
  id: string;
  type: 'agent_experience';
  agent_short_name: string;
  experience: 'insight_preview' | 'report_preview' | 'blog_refresh_queue' | 'content_outline_queue' | 'content_editor' | 'keyword_review' | 'experiments';
  data?: {
    title?: string;
    // insight_preview: inline blocks
    blocks?: (UIBlock | ContainerBlock | InsightCardBlock)[];
    // report_preview: meta for fetching & page selection
    insight_id?: string;
    insight_report_id?: string | number;
    page_group_id?: string;
    page_id?: string;
    // blog_refresh_queue, content_outline_queue: entries to store before fetching (trigger flow)
    entries?: unknown[];
    // content_editor: markdown content for rich text editing
    content?: string;
    // keyword_review: optional category filter (single or array of category keys)
    category?: string | string[];
    // experiments: experiment detail data
    description?: string;
    variant?: 'suggestion' | 'processing' | 'analytics';
    experiment_id?: number;
    channel?: string;
    start_date?: string;
    end_date?: string;
    status?: 'great' | 'underperforming' | 'neutral';
    performance_overview?: ExperimentPerformanceMetric[];
    issues?: ExperimentIssue[];
    performance_trend?: ExperimentChartConfig;
    variant_comparison?: ExperimentChartConfig;
    detailed_metrics?: ExperimentDetailedMetricRow[];
    ai_insights?: ExperimentAiInsight[];
    follow_up_experiments?: ExperimentFollowUp[];
    expected_results?: ExperimentExpectedResult[];
    what_it_does?: string;
    channel_badge_variant?: string;
    // Context to send via UPDATE_CONTEXT before the user message
    platform_context?: unknown;
  };
};

export type BehaviourAction = BehaviourChatAction | BehaviourAgentExperienceAction;

/**
 * A behaviour attached to a block.
 * - trigger: 'auto' renders the slot immediately with the block
 * - trigger: 'click' renders the slot on user interaction (future)
 */
export type BlockBehaviour = {
  id: string;
  trigger: 'click' | 'auto';
  action: BehaviourAction;
};

// Content List Block Types — shared with backend via @strivelabs/ui-components-prompt-registry
export type {
  ContentListStepEntry,
  ContentListActionItem,
  ContentListItemContent,
  ContentListContent,
} from '@strivelabs/ui-components-prompt-registry';

// Plan Card Block Types — shared with backend via @strivelabs/ui-components-prompt-registry
export type { PlanCardContent } from '@strivelabs/ui-components-prompt-registry';
import type { PlanCardContent } from '@strivelabs/ui-components-prompt-registry';

export type PlanCardBlock = {
  id?: string;
  type: 'plan_card';
  version: 'v1';
  content: PlanCardContent;
};

// Re-import for local use in union types
import type { ContentListContent } from '@strivelabs/ui-components-prompt-registry';

export type ContentListBlock = {
  type: 'content_list';
  version: string;
  content: ContentListContent;
};

// ---------------------------------------------------------------------------
// Create-routine flow blocks (ui_text_input / ui_schedule_input /
// ui_channel_input / ui_routine_summary / ui_update_notice)
// ---------------------------------------------------------------------------

// Text Input Block (single free-text ask card)
export type TextInputContent = {
  title: string;
  description?: string | null;
  /** Pre-filled answer from the LLM for the user to confirm/edit. */
  value?: string | null;
  placeholder?: string | null;
  submit_label?: string | null;
  /** Rehydrated from the stored tool output: the answer the user submitted. */
  submittedValue?: string;
};

export type TextInputBlock = {
  type: "text_input";
  version: string;
  content: TextInputContent;
};

// Schedule Input Block (routine cadence + time picker card)
export type ScheduleFrequency = "daily" | "weekdays" | "weekly" | "monthly" | "custom";

export type ScheduleDay = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type ScheduleCustomConfig = {
  interval?: number | null;
  unit?: "day" | "week" | "month" | null;
  days_of_week?: ScheduleDay[] | null;
};

/** The schedule card's submitted output (also the tool-call output shape). */
export type ScheduleInputResult = {
  frequency: ScheduleFrequency;
  time: string;
  cron: string;
  label: string;
};

export type ScheduleInputContent = {
  title?: string | null;
  description?: string | null;
  frequency?: ScheduleFrequency | null;
  time?: string | null;
  custom?: ScheduleCustomConfig | null;
  /** Rehydrated from the stored tool output: the confirmed schedule. */
  submittedSchedule?: ScheduleInputResult;
};

export type ScheduleInputBlock = {
  type: "schedule_input";
  version: string;
  content: ScheduleInputContent;
};

// Channel Input Block (routine alert-delivery picker card)
export type ChannelInputPreselect = {
  slack?: boolean | null;
  google_chat?: boolean | null;
  email?: boolean | null;
  slack_channel_names?: string[] | null;
  google_chat_space_names?: string[] | null;
  /** Email addresses the user actually said — pre-filled as recipient chips. */
  emails?: string[] | null;
  /**
   * When editing an existing routine's delivery: its current output_channels,
   * forwarded verbatim — seeds the card with the exact saved selection.
   */
  output_channels?: { channels: ChannelInputOutputChannel[] } | null;
};

/** Wire shape of one selected delivery channel (mirrors routine output_channels). */
export type ChannelInputOutputChannel = {
  channel: "slack" | "google_chat" | "email";
  config?: {
    credential_id?: string;
    channels?: string[];
    spaces?: string[];
    recipients?: string[];
  };
};

/** The channel card's submitted output (also the tool-call output shape). */
export type ChannelInputResult = {
  output_channels: { channels: ChannelInputOutputChannel[] };
  summary: string;
};

export type ChannelInputContent = {
  title?: string | null;
  description?: string | null;
  preselect?: ChannelInputPreselect | null;
  /** Rehydrated from the stored tool output: the confirmed selection. */
  submittedChannels?: ChannelInputResult;
};

export type ChannelInputBlock = {
  type: "channel_input";
  version: string;
  content: ChannelInputContent;
};

// Routine Summary Block (final review card with Cancel / Create)
export type RoutineSummaryChannel = "strive_inbox" | "slack" | "google_chat" | "email";

export type RoutineSummaryContent = {
  title: string;
  description: string;
  alert_logic: string;
  schedule_label: string;
  delivers_to: { channel: RoutineSummaryChannel; detail?: string | null }[];
  confirm_label?: string | null;
  cancel_label?: string | null;
  /** Rehydrated from the stored tool output: the action the user took. */
  resolvedAction?: "create" | "cancel";
};

export type RoutineSummaryBlock = {
  type: "routine_summary";
  version: string;
  content: RoutineSummaryContent;
};

// Update Notice Block (display-only field-change acknowledgement rows)
export type UpdateNoticeItem = {
  field: string;
  verb: string;
  value?: string | null;
};

export type UpdateNoticeContent = {
  updates: UpdateNoticeItem[];
};

export type UpdateNoticeBlock = {
  type: "update_notice";
  version: string;
  content: UpdateNoticeContent;
};

// Task Cards Block (display-only receipt rows for tasks created via task_create)
export type TaskCardTask = {
  /**
   * The task's own id, which is what opens its details. Optional because a
   * conversation from before the id was carried rehydrates its cards from
   * stored tool arguments that have none — the renderer reads it back off the
   * `TASK-<id>` key in that case.
   */
  id?: string | null;
  key: string;
  title: string;
  assignee_name?: string | null;
  reviewer_name?: string | null;
};

export type TaskCardsContent = {
  tasks: TaskCardTask[];
};

export type TaskCardsBlock = {
  type: "task_cards";
  version: string;
  content: TaskCardsContent;
};

export type ArtifactCardContent = {
  /** Id of the artifact the card opens, as the authoring tool returned it. */
  artifact_id?: string;
  /**
   * What the tool result itself calls that id, under `artifact.id`. A model
   * copying from the result sends it flattened to `id` often enough that the
   * card reads either: it is a receipt, and it must not vanish from the
   * transcript over a key name.
   */
  id?: string | number | null;
  title: string;
  description?: string | null;
  /** Version at the time the card was rendered; the canvas reads the current one. */
  version?: number | string | null;
};

export type ArtifactCardBlock = {
  type: "artifact_card";
  version: string;
  content: ArtifactCardContent;
};

// Mermaid Diagram Block (display-only; content mirrors ui_mermaid_diagram's input)
export type MermaidDiagramContent = {
  title?: string | null;
  source: string;
  caption?: string | null;
};

export type MermaidDiagramBlock = {
  type: "mermaid_diagram";
  version: string;
  content: MermaidDiagramContent;
};

/**
 * Utility type to add optional behaviours to any block
 */
type WithBehaviours<T> = T & { behaviours?: BlockBehaviour[] };

// Union type for all supported blocks
export type UIBlock =
  | WithBehaviours<TextBlock>
  | WithBehaviours<ConfirmationBlock>
  | WithBehaviours<ConnectBlock>
  | WithBehaviours<SelectBlock>
  | WithBehaviours<RadioBlock>
  | WithBehaviours<EditableListBlock>
  | WithBehaviours<LineChartBlock>
  | WithBehaviours<BarChartBlock>
  | WithBehaviours<AreaChartBlock>
  | WithBehaviours<PieChartBlock>
  | WithBehaviours<RadialChartBlock>
  | WithBehaviours<ScatterChartBlock>
  | WithBehaviours<TableBlock>
  | WithBehaviours<DataTableClientBlock>
  | WithBehaviours<SelectableTableBlock>
  | WithBehaviours<StackedSuggestionsBlock>
  | WithBehaviours<InlineSuggestionsBlock>
  | WithBehaviours<MetricsGridBlock>
  | WithBehaviours<TabbedChartBlock>
  | WithBehaviours<ContentListBlock>
  | WithBehaviours<TextInputBlock>
  | WithBehaviours<ScheduleInputBlock>
  | WithBehaviours<ChannelInputBlock>
  | WithBehaviours<RoutineSummaryBlock>
  | WithBehaviours<UpdateNoticeBlock>
  | WithBehaviours<TaskCardsBlock>
  | WithBehaviours<ArtifactCardBlock>
  | WithBehaviours<MermaidDiagramBlock>
  | WithBehaviours<ContainerBlock>;

// Container response type
export type RenderPayload = {
  version: string;
  id?: string;
  title?: string;
  blocks: (UIBlock | ContainerBlock)[];
};

// UI Action Event Types (what gets sent back to the API)
export type UIActionEvent =
  | {
    type: "ui_action";
    component: "confirmation";
    label?: string;
    state: { action: { value: string; label: string }; confirmed: true };
  }
  | {
    type: "ui_action";
    component: "connect";
    label?: string;
    state:
      | { action: "use"; short_name: string; display_name: string }
      | { action: "dismiss" };
  }
  | {
    type: "ui_action";
    component: "select";
    label?: string;
    state: { values: string[]; labels?: string[] };
  }
  | {
    type: "ui_action";
    component: "radio";
    label?: string;
    state: { value: string; label: string };
  }
  | {
    type: "ui_action";
    component: "editable_list";
    label?: string;
    state: {
      mode: "string";
      items: string[];
      confirmed: true;
    } | {
      mode: "grouped";
      groups: EditableGroupedListGroup[];
      confirmed: true;
    };
  }
  | {
    type: "ui_action";
    component: "selectable_table";
    label?: string;
    state: {
      /** Stable ids of the selected rows (from content.idKey, or positions when idKey is omitted). */
      rowIds: (string | number)[];
      /** The selected rows' column values (cell objects flattened to their primitive values). */
      rows: Record<string, string | number | null>[];
      confirmed: true;
    };
  }
  | {
    type: "ui_action";
    component: "stacked_suggestions";
    label?: string;
    state: { text: string; prefillOnly: boolean };
  }
  | {
    type: "ui_action";
    component: "inline_suggestions";
    label?: string;
    state: { action: { value: string; label: string }; shouldRedirect: boolean };
  }
  | {
    type: "ui_action";
    component: "text_input";
    label?: string;
    state: { value: string };
  }
  | {
    type: "ui_action";
    component: "schedule_input";
    label?: string;
    state: ScheduleInputResult;
  }
  | {
    type: "ui_action";
    component: "channel_input";
    label?: string;
    state: ChannelInputResult;
  }
  | {
    type: "ui_action";
    component: "routine_summary";
    label?: string;
    state: { action: "create" | "cancel" };
  };

export interface Suggestion {
  id: number;
  text: string;
  icon?: React.ReactNode | string; // React node or lucide-react icon name
  /**  If true, only pre-fills the input. If false or undefined, auto-sends the message. Default: false (auto-send) */
  prefillOnly?: boolean;
}

// ============================================================================
// Insight Card Payload Types
// Aligns with API contract from fetchInsightReports (InsightReportData)
// ============================================================================

/**
 * Chat CTA content structure.
 *
 * When `context` is present, renders a banner with the context text and a
 * "Chat Now" button. When `context` is omitted, renders only the button —
 * useful when embedded inside other components such as `content_list` items.
 */
export type ChatCTAContent = {
  context?: string;
  button_text?: string;
};

/**
 * Block element - individual UI components within an insight card
 * Each element has a unique ID for layout mapping
 */
export type BlockElement = {
  id: string;
  type: "text" | "line_chart" | "bar_chart" | "area_chart" | "pie_chart" | "radial_chart" | "scatter_chart" | "table" | "metrics_grid" | "inline_suggestions" | "chat_cta" | "content_list";
  version: string;
  content: TextBlock["content"]
    | LineChartContent
    | BarChartContent
    | AreaChartContent
    | PieChartContent
    | RadialChartContent
    | ScatterChartContent
    | TableContent
    | MetricsGridContent
    | InlineSuggestionsContent
    | ChatCTAContent
    | ContentListContent;
  behaviours?: BlockBehaviour[];
};

/**
 * Layout configuration for insight card
 * Maps element IDs to left/right slots for 2-column layout
 * If not provided or incomplete, renders all blocks in single-column flow
 */
export type InsightCardLayout = {
  left?: string[];  // element IDs for left column (optional)
  right?: string[]; // element IDs for right column (stacked vertically, optional)
};

/**
 * Insight card configuration - visual and layout properties
 */
export type InsightCardConfig = {
  tags?: string[]; // optional badge pills above heading (e.g., campaign name, region)
  heading?: string; // supports markdown (e.g., "Leads are **declining**")
  heading_badge?: string; // optional badge shown next to heading
  border_color?: 'red' | 'green' | 'yellow' | 'gray' | 'blue' | 'purple'; // optional colored border
  layout?: InsightCardLayout; // optional layout config; if not provided, uses single-column flow
};

/**
 * Insight card block - contains structure, presentation, and data
 * Note: The API returns blocks with a 'config' property containing heading, border_color, layout
 */
export type InsightCardBlock = {
  id: string;
  type?: "insight_card";
  version?: string;
  config: InsightCardConfig;
  block_elements: BlockElement[];
};

/**
 * Free-flow block - renders directly without InsightCard wrapper
 * Similar to UIBlock but with optional id and config for report context
 * Used when layout-based rendering (InsightCard) is not required
 *
 * Example payload:
 * {
 *   "id": "unique-id",
 *   "type": "text",
 *   "config": {},
 *   "content": "### Markdown content here"
 * }
 */
export type FreeFlowBlock = {
  id?: string;
  config?: Record<string, unknown>;
} & UIBlock;

/**
 * Top-level chat CTA block — sibling to insight_card / plan_card on a page.
 * Renders below the page's tabbed content as a "continue in chat" prompt.
 *
 * Per page convention: at most one ChatCTABlock per page, placed last in the
 * page's blocks array. Item-level CTAs (one-per-recommendation) are embedded
 * inside `content_list` items via `ContentListItemContent.chat_cta` instead.
 */
export type ChatCTABlock = {
  id: string;
  type: "chat_cta";
  version: string;
  content: ChatCTAContent;
  behaviours?: BlockBehaviour[];
};

/**
 * Union type for blocks in reports
 * - InsightCardBlock: Layout-based rendering with InsightCard wrapper
 * - FreeFlowBlock: Direct rendering without wrapper (like chat blocks)
 * - PlanCardBlock: Agent analysis plan steps display
 * - ChatCTABlock: Page-level "continue in chat" CTA, rendered below tabs
 */
export type ReportBlock = InsightCardBlock | FreeFlowBlock | PlanCardBlock | ChatCTABlock;

/**
 * Complete payload for insight card rendering (v1 format for flat reports)
 * Aligns with InsightReportData from fetchInsightReports API
 * Backend explicitly returns type: "FLAT" for flat/single-page reports
 *
 * Supports two block formats:
 * - InsightCardBlock: Layout-based with block_elements (renders with InsightCard wrapper)
 * - FreeFlowBlock: Direct UIBlock rendering (renders without wrapper, like chat)
 */
export type InsightCardPayload = {
  type: "FLAT";
  ui: {
    blocks: ReportBlock[];
  };
};

// ============================================================================
// Insight Card Payload V2 Types (Multi-page support)
// ============================================================================

/**
 * Property definition at the page group level.
 * Defines the schema for properties that pages can reference.
 */
export type PageGroupPropertyDef = {
  id: string;
  display_name: string;
};

/**
 * Property value at the page level.
 * References a PageGroupPropertyDef by id and provides the actual value.
 */
export type PagePropertyValue = {
  id: string; // references PageGroupPropertyDef.id
  value: string | number;
};

/**
 * Layout configuration for explorer-type page groups.
 * Defines how sidebar and filters are constructed from page properties.
 */
export type PageGroupLayout = {
  id: string;
  name: string;
  type: 'explorer'; // extensible in future
  config: {
    sidebar: {
      group: {
        property: {
          id: string; // references PageGroupPropertyDef.id - used for grouping pages in sidebar
        };
      };
    };
    top_filter: {
      properties: Array<{
        id: string; // references PageGroupPropertyDef.id - used for filtering
      }>;
    };
  };
};

/**
 * Individual page within a page group.
 * Properties reference the definitions at the page group level.
 *
 * Supports two block formats:
 * - InsightCardBlock: Layout-based with block_elements (renders with InsightCard wrapper)
 * - FreeFlowBlock: Direct UIBlock rendering (renders without wrapper, like chat)
 */
export type Page = {
  id?: string;
  properties?: PagePropertyValue[];
  metadata?: Record<string, unknown>;
  blocks: ReportBlock[];
};

/**
 * Page group - contains layouts, property definitions, and pages
 */
export type PageGroup = {
  id: string;
  display_name?: string;
  layouts?: PageGroupLayout[];
  properties?: PageGroupPropertyDef[];
  pages: Page[];
};

/**
 * Complete payload for insight card rendering (v2 format)
 * Always represents a multi-page report with page groups and explorer layouts.
 * For flat/single-page reports, use InsightCardPayload (v1 format) instead.
 */
export type InsightCardPayloadV2 = {
  version: "v2";
  type: "MULTI_PAGE";
  metadata?: Record<string, unknown>;
  page_groups: PageGroup[];
};

// ============================================================================
// Type Guards for Report Data
// ============================================================================

/**
 * Type guard to check if report data is a FLAT report
 */
export function isFlatReportData(data: unknown): data is InsightCardPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'ui' in data &&
    typeof (data as InsightCardPayload).ui === 'object' &&
    (data as InsightCardPayload).ui !== null &&
    'blocks' in (data as InsightCardPayload).ui &&
    Array.isArray((data as InsightCardPayload).ui.blocks)
  );
}

/**
 * Type guard to check if report data is a MULTI_PAGE report
 */
export function isMultiPageReportData(data: unknown): data is InsightCardPayloadV2 {
  return (
    typeof data === 'object' &&
    data !== null &&
    'page_groups' in data &&
    Array.isArray((data as InsightCardPayloadV2).page_groups)
  );
}